// frontend/src/components/RepDashboard.js
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { getRepSummary, getRepPayouts, getRepOrders, getRepPending } from '../api/client';
import RepCalculator from './RepCalculator';

const fmt = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n);

// Item counts — whole numbers, but tolerate the rare fractional quantity.
const fmtQty = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
}).format(n);

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
}) : '—';

// Tier band dollar range, matching the comp plan document wording:
// "Below $100,000" / "$100,000 – $224,999" / "$400,000+"
const fmt$0 = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(n);
const fmtBandRange = (band) => {
  if (band.to == null) return `${fmt$0(band.from)}+`;
  if (band.from === 0) return `Below ${fmt$0(band.to)}`;
  return `${fmt$0(band.from)} – ${fmt$0(band.to - 1)}`;
};

// ─── Quarter boundary helper ──────────────────────────────────────────────────
// Accepts "2025-Q3" or null (falls back to current calendar quarter)
function getQuarterBounds(quarterStr) {
  let year, q;
  if (quarterStr) {
    [year, q] = quarterStr.split('-Q').map(Number);
  } else {
    const now = new Date();
    year = now.getFullYear();
    q = Math.floor(now.getMonth() / 3) + 1;
  }
  const startMonth = (q - 1) * 3;                          // 0=Jan, 3=Apr, 6=Jul, 9=Oct
  const start = new Date(year, startMonth, 1);
  const end   = new Date(year, startMonth + 3, 0, 23, 59, 59); // last moment of last day
  return { start, end };
}

// The two tabs of "My Dashboard". My Earnings is the reporting view (what you
// earned); My Calculator is the explain-and-plan view (how it was calculated, and
// what-if). The quarter selector in the header drives both.
const TABS = ['My Earnings', 'My Calculator'];

// `repId` is optional: a global_admin passes an AE's NetSuite rep ID to view that
// rep's dashboard. Omitted for a rep viewing their own (backend uses the session).
export default function RepDashboard({ repId = null }) {
  const [quarter, setQuarter] = useState(null); // null = current quarter (backend default)
  const [tab, setTab] = useState(TABS[0]);

  const { data: summary, loading: sumLoading } = useApi(() => getRepSummary(quarter, repId), [quarter, repId]);

  if (sumLoading) return <DashboardSkeleton />;

  if (!summary) return (
    <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
      No commission data available. Contact Finance Admin to link your NetSuite rep ID.
    </div>
  );

  return (
    <div className="page-enter page-wrap" style={{ maxWidth: '1200px' }}>

      {/* Header */}
      <div className="page-header">
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>
            {summary?.quarter || 'Loading...'} · Commission Dashboard
          </div>
          <h1 style={{ fontSize: '1.6rem' }}>My Dashboard</h1>
        </div>
        <select
          value={quarter || ''}
          onChange={e => setQuarter(e.target.value || null)}
          className="input"
          style={{ width: 'auto', minWidth: '140px' }}
        >
          <option value="">Current Quarter</option>
          {generateQuarterOptions().map(q => (
            <option key={q} value={q}>{q}</option>
          ))}
        </select>
      </div>

      {/* Tabs — horizontal scroll on phones instead of wrapping */}
      <div style={{
        display: 'flex', gap: '4px',
        borderBottom: '1px solid var(--border)',
        marginBottom: '28px',
        overflowX: 'auto',
      }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 20px',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: tab === t ? 600 : 400,
              color: tab === t ? 'var(--text-primary)' : 'var(--text-muted)',
              borderBottom: `2px solid ${tab === t ? 'var(--blue-primary)' : 'transparent'}`,
              marginBottom: '-1px',
              transition: 'all 0.15s',
              fontFamily: 'var(--font-ui)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'My Earnings' && (
        <MyEarnings key={summary.quarter} summary={summary} quarter={quarter} repId={repId} />
      )}

      {tab === 'My Calculator' && (
        // Pass the RESOLVED quarter from the summary, not the raw selector value,
        // so "Current Quarter" (null) doesn't leave the calculator guessing.
        <RepCalculator quarter={summary.quarter} repId={repId} />
      )}
    </div>
  );
}

// ─── Tab 1: My Earnings ───────────────────────────────────────────────────────
// Unchanged from the pre-tab dashboard: KPIs, tier progress, payout schedule,
// shipped orders, pending shipments.
function MyEarnings({ summary, quarter, repId }) {
  const { data: payoutsData, loading: payLoading } = useApi(() => getRepPayouts(quarter, repId), [quarter, repId]);
  const { data: ordersData, loading: ordLoading } = useApi(() => getRepOrders({ repId }), [repId]);
  const { data: pendingData, loading: pendLoading } = useApi(() => getRepPending(repId), [repId]);

  const { kpis, nextPayout, tierProgress } = summary;

  // Month picked on the Payout Schedule (YYYY-MM) — narrows Shipped Orders to the
  // lines credited that month. null = show the whole quarter.
  const [selectedMonth, setSelectedMonth] = useState(null);

  return (
    <>
      {/* ── Next Payout Banner ── */}
      <NextPayoutBanner payout={nextPayout} />

      {/* ── Comp plan (only shown when the rep has a plan history) ── */}
      <PlanBanner plan={summary.plan} />

      {/* ── KPI Cards ── */}
      <div className="kpi-grid-6" style={{ gap: '12px', marginBottom: '28px' }}>
        <KpiCard label="HW/SW QTD GP"              value={fmt(kpis?.hwSwQtdGP)}          accent="blue" />
        <KpiCard label="Prof Services QTD GP"      value={fmt(kpis?.servicesQtdGP)}      accent="blue" />
        <KpiCard label="Managed Svcs (MSA) QTD GP" value={fmt(kpis?.msaQtdGP)}           accent="blue" />
        <KpiCard label="Renewal MSA QTD GP"        value={fmt(kpis?.renewalMsaQtdGP)}    accent="blue" />
        <KpiCard label="FOD QTD GP"                value={fmt(kpis?.fodQtdGP)}           accent="blue" />
        <KpiCard label="Commission Earned QTD"     value={fmt(kpis?.totalCommissionQTD)} accent="green" />
      </div>

      {/* ── Tier Progress — hidden for reps entirely on the flat Managed Services plan ── */}
      {tierProgress && summary.plan?.showTier !== false && <TierProgress tier={tierProgress} />}

      {/* ── Payout Schedule Table ── */}
      <div className="card" style={{ marginBottom: '24px', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3>Payout Schedule — {summary.quarter}</h3>
            <div className="label" style={{ marginTop: '4px' }}>
              Click a month to see only the shipped orders behind it
            </div>
          </div>
        </div>
        {payLoading
          ? <TableSkeleton rows={3} />
          : <PayoutTable payouts={payoutsData?.payouts} selectedMonth={selectedMonth} onSelectMonth={setSelectedMonth} />}
      </div>

      {/* ── Shipped Orders ── */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h3>
            Shipped Orders
            {selectedMonth && <span style={{ color: 'var(--blue-primary)' }}> — {fmtMonth(selectedMonth)}</span>}
          </h3>
          {selectedMonth && (
            <button className="btn btn-ghost btn-sm" onClick={() => setSelectedMonth(null)}>
              ✕ Show all {summary.quarter}
            </button>
          )}
        </div>
        {/* FIX 2: pass the selected quarter down so HW/SW tab can scope by date */}
        {ordLoading
          ? <TableSkeleton rows={6} />
          : <OrdersTable orders={ordersData?.orders} quarter={quarter} monthFilter={selectedMonth} />}
      </div>

      {/* ── Pending Shipment ── */}
      <div className="card" style={{ overflow: 'hidden', marginTop: '24px' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3>Pending Shipment</h3>
          <div className="label" style={{ marginTop: '4px' }}>
            Closed-won opportunities (closed 7/1/2026 or later) with items not yet shipped
          </div>
        </div>
        {pendLoading ? <TableSkeleton rows={4} /> : <PendingTable pending={pendingData?.pending} />}
      </div>
    </>
  );
}

// ─── Next Payout Banner ────────────────────────────────────────────────────────
function NextPayoutBanner({ payout }) {
  if (!payout) return null;

  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--blue-deeper) 0%, var(--blue-dark) 100%)',
      border: '1px solid var(--border-bright)',
      borderRadius: 'var(--radius-xl)',
      padding: '28px 36px',
      marginBottom: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: 'var(--shadow-glow)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background shimmer */}
      <div style={{
        position: 'absolute', top: '-50%', right: '-10%',
        width: '300px', height: '300px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,121,194,0.2) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div>
        <div className="label" style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
          Next Commission Payout
        </div>
        <div style={{
          fontSize: '3rem', fontWeight: 700,
          fontFamily: 'var(--font-data)',
          color: 'white',
          letterSpacing: '-0.03em',
          lineHeight: 1,
          marginBottom: '8px',
        }}>
          {fmt(payout.amount)}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>
          Pay date: <strong style={{ color: 'white' }}>{fmtDate(payout.payDate)}</strong>
          <span style={{ margin: '0 10px', opacity: 0.4 }}>·</span>
          For {payout.month} shipments
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        <StatusBadge status={payout.status} large />
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, accent }) {
  const accentColor = {
    blue: 'var(--blue-primary)',
    green: 'var(--green)',
    muted: 'var(--text-muted)',
  }[accent] || 'var(--blue-primary)';

  return (
    <div className="card" style={{ padding: '18px 16px' }}>
      <div className="label" style={{ marginBottom: '10px', minHeight: '26px' }}>{label}</div>
      <div style={{
        fontSize: '1.25rem', fontWeight: 600,
        fontFamily: 'var(--font-data)',
        color: accentColor,
        letterSpacing: '-0.02em',
        whiteSpace: 'nowrap',
      }}>
        {value}
      </div>
    </div>
  );
}

// ─── Tier Progress ────────────────────────────────────────────────────────────
function TierProgress({ tier }) {
  const { bands, currentGP, currentTier, currentRate, toNextTier, nextTier } = tier;
  const maxGP = 500_000; // Display ceiling
  const pct = Math.min((currentGP / maxGP) * 100, 100);

  return (
    <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <div className="label" style={{ marginBottom: '4px' }}>HW/SW Quarterly Tier Progress</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <span style={{ fontSize: '1.4rem', fontWeight: 600, fontFamily: 'var(--font-data)', color: 'var(--blue-primary)' }}>
              {currentTier}
            </span>
            <span style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', fontFamily: 'var(--font-data)' }}>
              @ {currentRate}
            </span>
          </div>
        </div>
        {toNextTier != null && nextTier && (
          <div style={{ textAlign: 'right' }}>
            <div className="label" style={{ marginBottom: '4px' }}>To {nextTier.label} ({nextTier.rate})</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, fontFamily: 'var(--font-data)', color: 'var(--amber)' }}>
              {fmt(toNextTier)} away
            </div>
          </div>
        )}
        {!nextTier && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', color: 'var(--green)', fontWeight: 600, letterSpacing: '0.05em' }}>
              ✓ MAX TIER
            </div>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          height: '8px', background: 'var(--bg-elevated)',
          borderRadius: '4px', overflow: 'hidden', position: 'relative',
        }}>
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${pct}%`,
            background: 'linear-gradient(90deg, var(--blue-primary), #5b9bd5)',
            borderRadius: '4px',
            transition: 'width 0.6s ease',
            boxShadow: '0 0 8px rgba(0,121,194,0.6)',
          }} />
          {/* Threshold markers */}
          {[100_000, 225_000, 400_000].map(thresh => (
            <div key={thresh} style={{
              position: 'absolute',
              left: `${Math.min((thresh / maxGP) * 100, 100)}%`,
              top: '-3px', bottom: '-3px',
              width: '2px',
              background: 'var(--bg-base)',
              borderRadius: '1px',
            }} />
          ))}
        </div>
      </div>

      {/* Band labels */}
      <div className="tier-bands">
        {bands.map(band => (
          <div key={band.label} style={{
            flex: 1,
            padding: '8px 12px',
            background: band.label === currentTier ? 'var(--blue-subtle)' : 'var(--bg-elevated)',
            border: `1px solid ${band.label === currentTier ? 'var(--border-bright)' : 'var(--border)'}`,
            borderRadius: 'var(--radius-sm)',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '13px', fontWeight: 700,
              fontFamily: 'var(--font-data)',
              color: band.label === currentTier ? 'var(--blue-primary)' : 'var(--text-muted)',
            }}>
              {band.rate}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {band.label}
            </div>
            <div style={{
              fontSize: '10.5px',
              fontFamily: 'var(--font-data)',
              color: band.label === currentTier ? 'var(--blue-primary)' : 'var(--text-secondary)',
              marginTop: '3px',
              whiteSpace: 'nowrap',
            }}>
              {fmtBandRange(band)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Payout Table ─────────────────────────────────────────────────────────────
// Rows are clickable: selecting a month filters Shipped Orders below to the lines
// credited in that month, so a rep can tie the schedule row to its orders.
// Clicking the selected month again clears the filter.
function PayoutTable({ payouts, selectedMonth = null, onSelectMonth = () => {} }) {
  if (!payouts?.length) return <EmptyState message="No payout data for this quarter yet." />;

  // Only show the Plan column when it carries information.
  const showPlan = payouts.some(p => p.plan_mix && p.plan_mix !== 'rep');

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Month</th>
            <th style={{ textAlign: 'right' }}>HW/SW GP</th>
            <th style={{ textAlign: 'right' }}>Services GP</th>
            <th style={{ textAlign: 'right' }}>HW/SW Commission</th>
            <th style={{ textAlign: 'right' }}>Services Commission</th>
            <th style={{ textAlign: 'right' }}>FOD Commission</th>
            {showPlan && <th style={{ textAlign: 'right' }}>MS Plan Commission</th>}
            <th style={{ textAlign: 'right' }}>Total Payout</th>
            {showPlan && <th>Plan</th>}
            <th>Pay Date</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {payouts.map(p => {
            const selected = p.payout_month === selectedMonth;
            return (
              <tr
                key={p.payout_month}
                onClick={() => onSelectMonth(selected ? null : p.payout_month)}
                title={selected ? 'Click to show all shipped orders' : `Click to show only ${fmtMonth(p.payout_month)} shipped orders`}
                style={{
                  cursor: 'pointer',
                  background: selected ? 'var(--blue-subtle)' : undefined,
                  boxShadow: selected ? 'inset 3px 0 0 var(--blue-primary)' : undefined,
                }}
              >
                <td style={{ color: selected ? 'var(--blue-primary)' : 'var(--text-primary)', fontWeight: selected ? 700 : 500, whiteSpace: 'nowrap' }}>
                  {p.payout_month}
                  <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>{selected ? '▾' : '▸'}</span>
                </td>
                <td className="td-number">{fmt(p.hw_sw_gp_this_month)}</td>
                <td className="td-number">{fmt(p.services_gp_this_month)}</td>
                <td className="td-number">{fmt(p.hw_sw_payout)}</td>
                <td className="td-number">{fmt(p.services_payout)}</td>
                <td className="td-number">{fmt(p.fod_payout)}</td>
                {showPlan && (
                  <td className="td-number" style={{ color: p.ms_total_payout > 0 ? 'var(--blue-sky)' : 'var(--text-muted)' }}>
                    {fmt(p.ms_total_payout)}
                  </td>
                )}
                <td className="td-number" style={{ color: 'var(--green)', fontWeight: 600 }}>
                  {fmt(p.total_payout)}
                </td>
                {showPlan && (
                  <td title="AE-plan commission columns are to the left; this month's total is AE + MS plan">
                    <PlanBadge mix={p.plan_mix} />
                  </td>
                )}
                <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{fmtDate(p.pay_date)}</td>
                <td><StatusBadge status={p.status} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// "2026-09" → "September 2026"
function fmtMonth(ym) {
  const [y, m] = (ym || '').split('-').map(Number);
  if (!y || !m) return ym || '';
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// ─── Orders Table ─────────────────────────────────────────────────────────────
// One row per ORDER (grouped by the backend). Tabs rescope the money shown:
//   All      → the order's combined revenue/GP, with chips for the categories present
//   Category → only orders that shipped in that category, showing that category's
//              revenue/GP; the Categories column is hidden.
// Clicking a row expands it to the underlying line items, scoped to the active tab.
//
// `monthFilter` (YYYY-MM, from the Payout Schedule) narrows everything to the LINES
// credited in that month: an order's HW/SW and Services streams can be credited in
// different months, so the order row's revenue/GP are recomputed from just those
// lines — which is what makes the totals reconcile to the schedule row.
const CAT_COLOR = {
  HWSW: 'var(--blue-primary)', SERVICES: 'var(--green)',
  MSA: 'var(--text-accent)', RENEWAL_MSA: 'var(--text-secondary)', FOD: 'var(--amber)',
};
const CAT_LABEL = {
  HWSW: 'HW/SW', SERVICES: 'Prof Services',
  MSA: 'Managed Svcs', RENEWAL_MSA: 'Renewal MSA', FOD: 'FOD',
};
const CAT_ORDER = ['HWSW', 'SERVICES', 'MSA', 'RENEWAL_MSA', 'FOD'];

// Re-derive an order's chips/totals from a subset of its lines.
function scopeOrderToLines(o, lines) {
  const byCategory = {};
  const total = { revenue: 0, gp: 0 };
  let shipDate = null;
  for (const l of lines) {
    if (!byCategory[l.category]) byCategory[l.category] = { revenue: 0, gp: 0 };
    byCategory[l.category].revenue += l.revenue;
    byCategory[l.category].gp      += l.gp;
    total.revenue += l.revenue;
    total.gp      += l.gp;
    if (l.shipDate && (!shipDate || l.shipDate > shipDate)) shipDate = l.shipDate;
  }
  return {
    ...o,
    shipDate: shipDate || o.shipDate,
    categories: CAT_ORDER.filter(c => byCategory[c]),
    byCategory,
    total,
    lines,
  };
}

function OrdersTable({ orders, quarter, monthFilter = null }) {
  const [activeCat, setActiveCat]         = useState('ALL');
  const [expandedOrder, setExpandedOrder] = useState(null);

  if (!orders?.length) return <EmptyState message="No shipped orders found." />;

  const { start, end } = getQuarterBounds(quarter);

  // Quarter-scope by the order's (latest) ship date, then — if a month is picked
  // on the Payout Schedule — cut each order down to the lines credited that month.
  const inQuarter = orders.filter(o => {
    const d = new Date(o.shipDate);
    return d >= start && d <= end;
  });
  const inScope = monthFilter
    ? inQuarter
        .map(o => scopeOrderToLines(o, o.lines.filter(l => (l.shipDate || '').slice(0, 7) === monthFilter)))
        .filter(o => o.lines.length > 0)
    : inQuarter;

  // Category-scope: on a category tab, drop orders that never shipped in that category.
  const visible = activeCat === 'ALL'
    ? inScope
    : inScope.filter(o => o.byCategory?.[activeCat]);

  const showCatCol = activeCat === 'ALL';
  const midSpan    = showCatCol ? 4 : 3; // columns the expanded item cell spans

  // Month reconciliation strip: GP by category across everything in scope, so the
  // rep can line it up against the schedule row they clicked.
  const monthGp = monthFilter ? inScope.reduce((acc, o) => {
    for (const [c, m] of Object.entries(o.byCategory)) acc[c] = (acc[c] || 0) + m.gp;
    return acc;
  }, {}) : null;

  return (
    <div>
      {monthFilter && (
        <div style={{
          padding: '12px 24px', borderBottom: '1px solid var(--border)',
          background: 'var(--blue-subtle)',
          display: 'flex', gap: '18px', alignItems: 'center', flexWrap: 'wrap',
          fontSize: '12.5px', color: 'var(--text-secondary)',
        }}>
          <span>
            <strong style={{ color: 'var(--text-primary)' }}>{inScope.length}</strong> order{inScope.length === 1 ? '' : 's'} with lines credited in{' '}
            <strong style={{ color: 'var(--text-primary)' }}>{fmtMonth(monthFilter)}</strong>
          </span>
          {CAT_ORDER.filter(c => monthGp[c]).map(c => (
            <span key={c} style={{ fontFamily: 'var(--font-data)', whiteSpace: 'nowrap' }}>
              <CategoryChip category={c} /> <span style={{ color: 'var(--text-primary)' }}>{fmt(monthGp[c])}</span>
            </span>
          ))}
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-data)', whiteSpace: 'nowrap' }}>
            GP total <strong style={{ color: 'var(--green)' }}>{fmt(Object.values(monthGp).reduce((s, v) => s + v, 0))}</strong>
          </span>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ padding: '12px 24px', display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)' }}>
        {['ALL', ...CAT_ORDER].map(f => (
          <button
            key={f}
            onClick={() => { setActiveCat(f); setExpandedOrder(null); }}
            className="btn btn-ghost btn-sm"
            style={{
              background: activeCat === f ? 'var(--blue-subtle)' : 'transparent',
              borderColor: activeCat === f ? 'var(--border-bright)' : 'transparent',
              color: activeCat === f ? 'var(--text-primary)' : 'var(--text-muted)',
            }}
          >
            {f === 'ALL' ? 'All' : CAT_LABEL[f]}
          </button>
        ))}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '28px' }}></th>
              <th>Ship Date</th>
              <th>Order #</th>
              <th>Customer</th>
              {showCatCol && <th>Categories</th>}
              <th style={{ textAlign: 'right' }}>Revenue</th>
              <th style={{ textAlign: 'right' }}>GP</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(o => {
              const money    = showCatCol ? o.total : o.byCategory[activeCat];
              const expanded = expandedOrder === o.orderNumber;
              const lines    = showCatCol ? o.lines : o.lines.filter(l => l.category === activeCat);

              return (
                <React.Fragment key={o.orderNumber}>
                  <tr
                    onClick={() => setExpandedOrder(expanded ? null : o.orderNumber)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{expanded ? '▾' : '▸'}</td>
                    <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{fmtDate(o.shipDate)}</td>
                    <td style={{ fontFamily: 'var(--font-data)', fontSize: '12px', color: 'var(--text-accent)', whiteSpace: 'nowrap' }}>
                      {o.orderNumber}
                      {o.plans?.includes('msa') && <PlanBadge mix={o.plans.length > 1 ? 'mixed' : 'msa'} small />}
                    </td>
                    <td style={{ color: 'var(--text-primary)' }}>{o.customer}</td>
                    {showCatCol && (
                      <td>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {o.categories.map(c => <CategoryChip key={c} category={c} />)}
                        </div>
                      </td>
                    )}
                    <td className="td-number">{fmt(money?.revenue)}</td>
                    <td className="td-number" style={{ color: 'var(--green)' }}>{fmt(money?.gp)}</td>
                  </tr>

                  {expanded && lines.map((l, i) => (
                    <tr key={i} style={{ background: 'var(--bg-elevated)' }}>
                      <td></td>
                      <td colSpan={midSpan} style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          <CategoryChip category={l.category} />
                          {l.plan === 'msa' && <PlanBadge mix="msa" small />}
                          <span style={{ maxWidth: '340px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.item}
                          </span>
                          {l.shipDate && (
                            <span style={{ fontSize: '11.5px', color: 'var(--text-muted)', fontFamily: 'var(--font-data)', whiteSpace: 'nowrap' }}>
                              {fmtDate(l.shipDate)}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="td-number" style={{ color: 'var(--text-secondary)' }}>{fmt(l.revenue)}</td>
                      <td className="td-number" style={{ color: 'var(--text-secondary)' }}>{fmt(l.gp)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {visible.length === 0 && (
        <EmptyState message={
          monthFilter
            ? `No ${activeCat === 'ALL' ? '' : (CAT_LABEL[activeCat] + ' ')}lines were credited in ${fmtMonth(monthFilter)}.`
            : `No ${activeCat === 'ALL' ? '' : (CAT_LABEL[activeCat] + ' ')}orders found for ${quarter || 'the current quarter'}.`
        } />
      )}
    </div>
  );
}

// Which comp plan paid a row: 'rep' (AE plan, the default — usually not shown),
// 'msa' (Managed Services plan, flat rates) or 'mixed' (both in one month/order).
function PlanBadge({ mix, small = false }) {
  if (!mix || mix === 'rep') return small ? null : <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>AE plan</span>;
  const label = mix === 'msa' ? 'MS plan' : 'Mixed';
  return (
    <span style={{
      display: 'inline-block', marginLeft: small ? '6px' : 0,
      fontSize: small ? '9.5px' : '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      padding: small ? '1px 5px' : '2px 8px', borderRadius: '8px',
      background: mix === 'msa' ? 'rgba(57, 185, 223, 0.14)' : 'var(--amber-subtle)',
      color: mix === 'msa' ? 'var(--blue-sky)' : 'var(--amber)',
      verticalAlign: 'middle',
    }}>
      {label}
    </span>
  );
}

// "Your plan" strip. Only renders when the rep has a plan history — reps who have
// only ever been on the AE plan never see it.
function PlanBanner({ plan }) {
  if (!plan?.assignments?.length) return null;
  const fmtD = (d) => fmtDate(d);
  const rows = [...plan.assignments].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  return (
    <div style={{
      marginBottom: '20px', padding: '12px 18px',
      background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderLeft: '3px solid var(--blue-sky)',
      borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6,
    }}>
      <strong style={{ color: 'var(--text-primary)' }}>Your comp plan:</strong>{' '}
      {rows.map((a, i) => (
        <span key={a.effectiveFrom}>
          {i > 0 && ' · '}
          <strong style={{ color: a.plan === 'msa' ? 'var(--blue-sky)' : 'var(--text-primary)' }}>{plan.labels?.[a.plan] || a.plan}</strong>
          {' '}for deals closed on/after {fmtD(a.effectiveFrom)}
        </span>
      ))}
      {rows[0] && <span> · <strong style={{ color: 'var(--text-primary)' }}>AE plan</strong> for anything closed before {fmtD(rows[0].effectiveFrom)}</span>}
      <span style={{ color: 'var(--text-muted)' }}> — decided by each deal's close date; still paid in the month it ships.</span>
    </div>
  );
}

// Small colored category pill, reused for the order chips and the line sub-rows.
function CategoryChip({ category }) {
  return (
    <span style={{
      fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em',
      color: CAT_COLOR[category] || 'var(--text-muted)',
      textTransform: 'uppercase',
    }}>
      {CAT_LABEL[category] || category}
    </span>
  );
}

// ─── Pending Shipment Table ───────────────────────────────────────────────────
// Fulfilled / Fulfillment Total are ITEM QUANTITIES (0 fulfilled = nothing has
// shipped yet). Opp GP is the total est GP of the whole opportunity.
function PendingTable({ pending }) {
  if (!pending?.length) {
    return <EmptyState message="No pending shipments — every closed-won opportunity has fully shipped." />;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Opp #</th>
            <th style={{ textAlign: 'right' }}>Fulfilled</th>
            <th style={{ textAlign: 'right' }}>Fulfillment Total</th>
            <th>Estimated Ship Date</th>
            <th style={{ textAlign: 'right' }}>Opp GP</th>
          </tr>
        </thead>
        <tbody>
          {pending.map((p, i) => (
            <tr key={i}>
              <td style={{ color: 'var(--text-primary)', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.opp_title || '—'}
              </td>
              <td style={{ fontFamily: 'var(--font-data)', fontSize: '12px', color: 'var(--text-accent)' }}>
                {p.opp_number}
              </td>
              <td className="td-number">{fmtQty(p.fulfilled_qty)}</td>
              <td className="td-number">{fmtQty(p.total_qty)}</td>
              <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{fmtDate(p.estimated_ship_date)}</td>
              <td className="td-number" style={{ color: 'var(--green)' }}>{fmt(p.total_gp)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Shared components ────────────────────────────────────────────────────────
function StatusBadge({ status, large }) {
  const map = {
    paid:     { cls: 'badge-paid',     label: 'Paid' },
    approved: { cls: 'badge-approved', label: 'Approved' },
    pending:  { cls: 'badge-pending',  label: 'Pending' },
    disputed: { cls: 'badge-disputed', label: 'Disputed' },
    clawback: { cls: 'badge-disputed', label: 'Clawback' },
  };
  const { cls, label } = map[status] || { cls: '', label: status };
  return (
    <span className={`badge ${cls}`} style={large ? { fontSize: '13px', padding: '5px 14px' } : {}}>
      {label}
    </span>
  );
}

function EmptyState({ message }) {
  return (
    <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
      {message}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="page-wrap" style={{ maxWidth: '1200px' }}>
      <div className="skeleton" style={{ height: '24px', width: '200px', marginBottom: '8px' }} />
      <div className="skeleton" style={{ height: '36px', width: '300px', marginBottom: '32px' }} />
      <div className="skeleton" style={{ height: '120px', borderRadius: '24px', marginBottom: '28px' }} />
      <div className="kpi-grid-4" style={{ marginBottom: '28px' }}>
        {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: '90px', borderRadius: '16px' }} />)}
      </div>
      <div className="skeleton" style={{ height: '160px', borderRadius: '16px', marginBottom: '24px' }} />
      <div className="skeleton" style={{ height: '280px', borderRadius: '16px' }} />
    </div>
  );
}

function TableSkeleton({ rows = 4 }) {
  return (
    <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height: '36px', borderRadius: '6px' }} />
      ))}
    </div>
  );
}

function generateQuarterOptions() {
  const opts = [];
  const now = new Date();
  for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
    for (let q = 4; q >= 1; q--) {
      opts.push(`${y}-Q${q}`);
    }
  }
  return opts;
}
